import { useCallback, useEffect, useMemo, useState } from "react"
import { ReactSortable } from "react-sortablejs"
import TextareaAutosize from "react-textarea-autosize"
import api from "../api"
import { ReactComponent as AddIcon } from "../assets/images/icons/ic-add-circle.svg"
import { ReactComponent as DeleteIcon } from "../assets/images/icons/ic-delete.svg"
import { ReactComponent as EditIcon } from "../assets/images/icons/ic-edit.svg"
import { ButtonStatus, CourseType } from "../common/constants"
import Button from "./Button"
import Select from "./Select"
import Skeleton from "./Skeleton"
import TextInput from "./TextInput"
import Toggle from "./Toggle"
import AlertDialog from "./dialogs/AlertDialog"
import Dialog from "./dialogs/Dialog"
import styles from "./ApplicationsManagerDialog.module.css"

const EMPTY_APPLICATION = {
  name: "",
  title: "",
  subtitle: "",
  questions: [],
  status: "draft",
  expired_at: null,
  course_id: null,
  send_confirmation_email: false,
  confirmation_delay: 0,
  pipeline_id: null,
  mail_type: "application",
  owner_id: null,
}

const STATUS_OPTIONS = [
  { id: 0, name: "Bozza", value: "draft" },
  { id: 1, name: "Pubblica", value: "public" },
  { id: 2, name: "Nascosta", value: "hidden" },
]

const MAIL_TYPE_OPTIONS = [
  { id: 0, name: "Candidatura", value: "application" },
  { id: 1, name: "Richiesta informazioni", value: "info" },
]

const CONFIRMATION_DELAY_OPTIONS = [
  { id: 0, name: "Immediata", value: 0 },
  { id: 1, name: "30 minuti", value: 30 },
  { id: 2, name: "1 ora", value: 60 },
  { id: 3, name: "2 ore", value: 120 },
  { id: 4, name: "6 ore", value: 360 },
  { id: 5, name: "12 ore", value: 720 },
  { id: 6, name: "24 ore", value: 1440 },
  { id: 7, name: "48 ore", value: 2880 },
  { id: 8, name: "72 ore", value: 4320 },
  { id: 9, name: "1 settimana", value: 10080 },
]

const STATUS_LABELS = { draft: "Bozza", public: "Pubblica", hidden: "Nascosta" }

const normalizeApplication = application => ({
  ...EMPTY_APPLICATION,
  ...application,
  questions: Array.isArray(application?.questions) ? [...application.questions] : [],
  confirmation_delay: application?.confirmation_delay ?? 0,
  mail_type: application?.mail_type || "application",
})

const sanitizeApplication = application => ({
  name: application.name?.trim() || "",
  title: application.title?.trim() || null,
  subtitle: application.subtitle?.trim() || null,
  questions: (application.questions || []).map(question => question.trim()).filter(Boolean),
  status: application.status || "draft",
  expired_at: application.expired_at || null,
  course_id: application.course_id || null,
  send_confirmation_email: Boolean(application.send_confirmation_email),
  confirmation_delay: application.send_confirmation_email ? application.confirmation_delay ?? 0 : null,
  pipeline_id: application.pipeline_id || null,
  mail_type: application.mail_type || "application",
  owner_id: application.owner_id || null,
})

const toDateInputValue = value => {
  if (!value) return ""
  const date = new Date(String(value).endsWith("Z") ? value : `${value}Z`)
  if (Number.isNaN(date.getTime())) return ""
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
  return local.toISOString().slice(0, 16)
}

const ApplicationsManagerDialog = ({ onChanged }) => {
  const [open, setOpen] = useState(false)
  const [applications, setApplications] = useState([])
  const [courses, setCourses] = useState([])
  const [admins, setAdmins] = useState([])
  const [pipelines, setPipelines] = useState([])
  const [selectedPipeline, setSelectedPipeline] = useState(null)
  const [draft, setDraft] = useState(null)
  const [query, setQuery] = useState("")
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [error, setError] = useState("")

  const load = useCallback(async () => {
    setLoading(true)
    setError("")
    try {
      const [applicationRows, courseRows, adminRows] = await Promise.all([
        api.get("/admin/applications", { timeout: 0 }),
        api.get("/admin/courses?complete=false", { timeout: 0 }),
        api.get("/admin/admins", { timeout: 0 }),
      ])
      setApplications(applicationRows || [])
      setCourses((courseRows || []).map(course => ({ ...course, value: course.id })))
      setAdmins((adminRows || []).filter(admin => admin.active_id).map(admin => ({
        ...admin,
        value: admin.id,
        name: `${admin.name} ${admin.surname}`.trim(),
      })))
    } catch (e) {
      setError(e?.detail || "Non è stato possibile caricare le candidature.")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (open) load()
  }, [load, open])

  const filteredApplications = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("it")
    if (!normalized) return applications
    return applications.filter(application => {
      const course = courses.find(item => item.id === application.course_id)
      return `${application.name || ""} ${course?.name || ""}`.toLocaleLowerCase("it").includes(normalized)
    })
  }, [applications, courses, query])

  const selectedCourse = courses.find(course => course.id === draft?.course_id) || null
  const isWebinar = selectedCourse?.type === CourseType.Webinar
  const statusOptions = draft?.id && draft.status !== "draft"
    ? STATUS_OPTIONS.filter(option => option.value !== "draft")
    : STATUS_OPTIONS.filter(option => option.value !== "hidden")
  const isValid = Boolean(
    draft?.name?.trim()
    && (draft.status === "draft" || (draft.course_id && (isWebinar || draft.owner_id)))
  )

  const edit = async application => {
    const next = normalizeApplication(application)
    setDraft(next)
    setError("")
    setPipelines([])
    setSelectedPipeline(null)
    if (next.pipeline_id) {
      try {
        const pipeline = await api.get(`/admin/active-campaign/pipelines/${next.pipeline_id}`)
        const option = { ...pipeline, id: Number(pipeline.id), value: Number(pipeline.id), name: pipeline.title || pipeline.name }
        setSelectedPipeline(option)
        setPipelines([option])
      } catch (e) {
        setError(e?.detail || "La pipeline collegata non è più disponibile.")
      }
    }
  }

  const searchPipelines = async value => {
    try {
      const rows = await api.get(`/admin/active-campaign/pipelines?q=${encodeURIComponent(value || "")}`)
      const options = (rows || []).map(pipeline => ({
        ...pipeline,
        id: Number(pipeline.id),
        value: Number(pipeline.id),
        name: pipeline.title || pipeline.name,
      }))
      setPipelines(selectedPipeline
        ? [selectedPipeline, ...options.filter(option => option.id !== selectedPipeline.id)]
        : options)
    } catch (e) {
      setError(e?.detail || "Non è stato possibile cercare le pipeline.")
    }
  }

  const save = async () => {
    if (!isValid || saving) return
    setSaving(true)
    setError("")
    try {
      const payload = sanitizeApplication(draft)
      const saved = draft.id
        ? await api.put(`/admin/applications/${draft.id}`, payload, { timeout: 0 })
        : await api.post("/admin/applications", payload, { timeout: 0 })
      setApplications(current => draft.id
        ? current.map(application => application.id === saved.id ? saved : application)
        : [...current, saved])
      setDraft(normalizeApplication(saved))
      await onChanged?.()
    } catch (e) {
      setError(e?.detail || "Non è stato possibile salvare la candidatura.")
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!draft?.id) return
    setSaving(true)
    setError("")
    try {
      await api.delete(`/admin/applications/${draft.id}`, { timeout: 0 })
      setApplications(current => current.filter(application => application.id !== draft.id))
      setDraft(null)
      setDeleteOpen(false)
      await onChanged?.()
    } catch (e) {
      setError(e?.detail || "Non è stato possibile eliminare la candidatura.")
      setDeleteOpen(false)
    } finally {
      setSaving(false)
    }
  }

  const close = () => {
    if (saving) return
    setOpen(false)
    setDraft(null)
    setQuery("")
    setError("")
  }

  return <>
    <Button appearance="subtle" onClick={() => setOpen(true)}>
      <EditIcon className={styles.buttonIcon} /> Gestisci candidature
    </Button>
    <Dialog
      appearance="modern"
      open={open}
      onClose={close}
      title="Gestisci candidature"
      style={{ width: "min(1120px, 96vw)", height: "min(760px, 92dvh)", maxHeight: "92dvh", borderRadius: "16px" }}
      contentMaxHeight="none"
      overflow="hidden"
      contentStyle={{ padding: 0, alignItems: "stretch" }}
    >
      <div className={`${styles.manager} ${!draft ? styles.overview : ""}`}>
        <aside className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <div><strong>Candidature</strong><span>{applications.length} configurazioni</span></div>
            <button type="button" className={styles.addButton} onClick={() => { setDraft(normalizeApplication(EMPTY_APPLICATION)); setSelectedPipeline(null); setPipelines([]); setError("") }}>
              <AddIcon /> Nuova
            </button>
          </div>
          <TextInput appearance="modern" type="search" value={query} onKeyUp={setQuery} placeholder="Cerca candidatura o corso" />
          <div className={styles.applicationList}>
            {loading ? Array.from({ length: 5 }, (_, index) => <div className={styles.listSkeleton} key={index}><Skeleton width="70%" height="12px" /><Skeleton width="45%" height="9px" /></div>) : filteredApplications.map(application => {
              const course = courses.find(item => item.id === application.course_id)
              return <button
                type="button"
                key={application.id}
                className={`${styles.applicationItem} ${draft?.id === application.id ? styles.selected : ""}`}
                onClick={() => edit(application)}
              >
                <span><strong>{application.name}</strong><small>{course?.name || "Nessun corso associato"}</small></span>
                <span className={`${styles.status} ${styles[`status_${application.status}`] || ""}`}>{STATUS_LABELS[application.status] || application.status}</span>
              </button>
            })}
            {!loading && !filteredApplications.length && <div className={styles.emptyList}>Nessuna candidatura trovata.</div>}
          </div>
        </aside>

        {draft && <section className={styles.editor}>
          <>
            <header className={styles.editorHeader}>
              <div><span>{draft.id ? `Candidatura #${draft.id}` : "Nuova candidatura"}</span><h2>{draft.name || "Candidatura senza nome"}</h2></div>
              <span className={`${styles.status} ${styles[`status_${draft.status}`] || ""}`}>{STATUS_LABELS[draft.status]}</span>
            </header>

            <div className={styles.form}>
              <div className={styles.gridTwo}>
                <label className={styles.field}><span>Nome interno</span><TextInput appearance="modern" type="text" value={draft.name} onKeyUp={value => setDraft(current => ({ ...current, name: value }))} placeholder="Nome candidatura" /></label>
                <label className={styles.field}><span>Stato</span><Select appearance="modern" portal selected={statusOptions.find(option => option.value === draft.status)} options={statusOptions} onSelect={option => setDraft(current => ({ ...current, status: option.value }))} /></label>
                <label className={styles.field}><span>Corso</span><Select appearance="modern" portal nullable nullLabel="Nessun corso" searchPlaceholder="Cerca corso" selected={selectedCourse} options={courses.filter(course => course.requires_application)} onSelect={option => setDraft(current => ({ ...current, course_id: option?.id || null }))} /></label>
                <label className={styles.field}><span>Scadenza</span><input className={styles.nativeInput} type="datetime-local" value={toDateInputValue(draft.expired_at)} onChange={event => setDraft(current => ({ ...current, expired_at: event.target.value ? new Date(event.target.value).toISOString().split(".")[0] : null }))} /></label>
              </div>

              {!isWebinar && <div className={styles.section}>
                <div className={styles.sectionTitle}><div><h3>Gestione commerciale</h3><p>Collega la candidatura alla pipeline e al sales responsabile.</p></div></div>
                <div className={styles.gridTwo}>
                  <label className={styles.field}><span>Pipeline ActiveCampaign</span><Select appearance="modern" portal nullable nullLabel="Nessuna pipeline" placeholder="Cerca pipeline" searchPlaceholder="Cerca pipeline" selected={selectedPipeline} options={pipelines} onSearch={searchPipelines} onSelect={option => { setSelectedPipeline(option || null); setDraft(current => ({ ...current, pipeline_id: option?.id || null })) }} /></label>
                  <label className={styles.field}><span>Owner</span><Select appearance="modern" portal nullable nullLabel="Nessun owner" searchPlaceholder="Cerca owner" selected={admins.find(admin => admin.id === draft.owner_id) || null} options={admins} onSelect={option => setDraft(current => ({ ...current, owner_id: option?.id || null }))} /></label>
                </div>
              </div>}

              {!isWebinar && <div className={styles.section}>
                <div className={styles.sectionTitle}><div><h3>Email automatica</h3><p>Configura la conferma inviata dopo l’acquisizione.</p></div><Toggle checked={draft.send_confirmation_email} onChange={checked => setDraft(current => ({ ...current, send_confirmation_email: checked }))} /></div>
                <div className={styles.gridTwo}>
                  <label className={styles.field}><span>Tipo di email</span><Select appearance="modern" portal selected={MAIL_TYPE_OPTIONS.find(option => option.value === draft.mail_type)} options={MAIL_TYPE_OPTIONS} onSelect={option => setDraft(current => ({ ...current, mail_type: option.value }))} /></label>
                  {draft.send_confirmation_email && <label className={styles.field}><span>Invio</span><Select appearance="modern" portal selected={CONFIRMATION_DELAY_OPTIONS.find(option => option.value === draft.confirmation_delay)} options={CONFIRMATION_DELAY_OPTIONS} onSelect={option => setDraft(current => ({ ...current, confirmation_delay: option.value }))} /></label>}
                </div>
              </div>}

              <div className={styles.section}>
                <div className={styles.sectionTitle}><div><h3>Contenuto del form</h3><p>Titolo, descrizione e domande mostrate al candidato.</p></div></div>
                <label className={styles.field}><span>Titolo</span><TextInput appearance="modern" type="text" value={draft.title || ""} onKeyUp={value => setDraft(current => ({ ...current, title: value }))} placeholder="Titolo del form" /></label>
                <label className={styles.field}><span>Sottotitolo</span><TextareaAutosize className={styles.textarea} minRows={3} maxRows={8} value={draft.subtitle || ""} onChange={event => setDraft(current => ({ ...current, subtitle: event.target.value }))} placeholder="Descrivi brevemente la candidatura" /></label>
                <div className={styles.questionsHeader}><span>Domande</span><button type="button" onClick={() => setDraft(current => ({ ...current, questions: [...current.questions, ""] }))}><AddIcon /> Aggiungi domanda</button></div>
                <ReactSortable className={styles.questions} list={draft.questions} setList={questions => setDraft(current => ({ ...current, questions }))}>
                  {draft.questions.map((question, index) => <div className={styles.question} key={index}>
                    <span className={styles.dragHandle} aria-hidden="true">⋮⋮</span>
                    <TextareaAutosize minRows={2} maxRows={6} value={question} onChange={event => setDraft(current => ({ ...current, questions: current.questions.map((item, itemIndex) => itemIndex === index ? event.target.value : item) }))} placeholder={`Domanda ${index + 1}`} />
                    <button type="button" aria-label={`Elimina domanda ${index + 1}`} onClick={() => setDraft(current => ({ ...current, questions: current.questions.filter((_, itemIndex) => itemIndex !== index) }))}><DeleteIcon /></button>
                  </div>)}
                </ReactSortable>
                {!draft.questions.length && <div className={styles.noQuestions}>Non sono ancora state inserite domande.</div>}
              </div>
            </div>

            {error && <div className={styles.error} role="alert">{error}</div>}
            <footer className={styles.footer}>
              <div>{draft.id && draft.status === "draft" && <Button appearance="subtle" accentColor="var(--sf-red)" disabled={saving} onClick={() => setDeleteOpen(true)}>Elimina</Button>}</div>
              <div><Button appearance="subtle" disabled={saving} onClick={() => setDraft(null)}>Annulla</Button><Button disabled={!isValid || saving} status={saving ? ButtonStatus.Loading : undefined} onClick={save}>Salva candidatura</Button></div>
            </footer>
          </>
        </section>}
      </div>
    </Dialog>
    <AlertDialog
      appearance="modern"
      open={deleteOpen}
      title="Eliminare la candidatura?"
      text="La candidatura in bozza verrà eliminata definitivamente."
      onClose={() => setDeleteOpen(false)}
      actions={[
        { label: "Annulla", onClick: () => setDeleteOpen(false), inverse: true },
        { label: "Elimina", onClick: remove, accentColor: "var(--sf-red)" },
      ]}
    />
  </>
}

export default ApplicationsManagerDialog
